/**
 * A quote's broker-facing pricing on the compare page (S2BWB-274).
 *
 * The quote row carries the pricing the API worked out (S2BWB-272/-273/-274): the
 * effective values with the broker's edits applied, who set each value, and the
 * source's own value beside an edit. The Pricing dialog edits a form built from it and
 * saves the request built back from that form.
 *
 * Amounts travel as decimal strings and are added up here in whole cents, never as
 * floats. The totals follow the API's rule: removed lines never count; when the
 * premium came from SHAPE or UWB, only that source's lines and the broker's count;
 * otherwise every line counts.
 */
import moment from "moment";
import { formatCurrencyAmount, parseCurrencyAmount } from "@/config/currency";

export const FEE_TYPES = [
  { value: "tax", text: "Tax" },
  { value: "carrier_fee", text: "Carrier fee" },
  { value: "broker_fee", text: "Broker fee" },
  { value: "surcharge", text: "Surcharge" },
  { value: "other", text: "Other" },
];

/** The sources whose values a broker edit is kept beside. */
export const SOURCE_ORIGINS = ["shape", "uwb", "mshift"];

const SOURCE_LABELS = { shape: "SHAPE", uwb: "UWB", mshift: "MShift", broker: "Broker" };

export const PRICING_LIMITS = {
  feeLines: 30,
  label: 100,
  specialConditions: 2000,
  items: 20,
  item: 500,
};

// Amounts are below 10,000,000,000 (in cents, 10^12; still an exact Number).
const AMOUNT_LIMIT_CENTS = 1e12;
const RATE_RE = /^\d+(?:\.\d+)?$/;

export const sourceLabel = (source) => SOURCE_LABELS[source] || "";

export const isSourceValue = (source) => SOURCE_ORIGINS.includes(source);

const text = (value) => (value === null || value === undefined ? "" : String(value));

/** The pricing fields of a GET submission detail quote row, defaults for the rest. */
export function pricingFromApiQuote(q) {
  const row = q || {};
  return {
    currencyCode: row.currencyCode || "USD",
    basePremiumAmount: row.basePremiumAmount ?? null,
    basePremiumSource: row.basePremiumSource ?? null,
    basePremiumEdited: row.basePremiumEdited === true,
    basePremiumSourceAmount: row.basePremiumSourceAmount ?? null,
    basePremiumChangedAt: row.basePremiumChangedAt ?? null,
    feeLines: Array.isArray(row.feeLines) ? row.feeLines : [],
    totalTaxesAndFees: row.totalTaxesAndFees ?? null,
    totalCost: row.totalCost ?? null,
    sourceTotal: row.sourceTotal ?? null,
    totalMismatch: row.totalMismatch === true,
    commissionRate: row.commissionRate ?? null,
    commissionAmount: row.commissionAmount ?? null,
    quotedEffectiveDate: row.quotedEffectiveDate ?? null,
    quotedExpirationDate: row.quotedExpirationDate ?? null,
    quotedPeriodSource: row.quotedPeriodSource ?? null,
    quotedPeriodEdited: row.quotedPeriodEdited === true,
    quotedPeriodSourceEffectiveDate: row.quotedPeriodSourceEffectiveDate ?? null,
    quotedPeriodSourceExpirationDate: row.quotedPeriodSourceExpirationDate ?? null,
    quotedPeriodChangedAt: row.quotedPeriodChangedAt ?? null,
    expiresOn: row.expiresOn ?? null,
    specialConditions: row.specialConditions ?? null,
    itemsNeededToBind: Array.isArray(row.itemsNeededToBind) ? row.itemsNeededToBind : [],
  };
}

/** True when the quote has no pricing at all: the dialog shows its empty state. */
export function isPricingEmpty(pricing) {
  const p = pricing || {};
  return (
    !p.basePremiumAmount &&
    !(p.feeLines && p.feeLines.length) &&
    !p.commissionRate &&
    !p.commissionAmount &&
    !p.quotedEffectiveDate &&
    !p.expiresOn &&
    !p.specialConditions &&
    !(p.itemsNeededToBind && p.itemsNeededToBind.length)
  );
}

/** True when a pricing amount is present (non-null, non-blank after trim). */
export function hasPricingAmount(value) {
  if (value === null || value === undefined) return false;
  return String(value).trim() !== "";
}

const UWB_FEE_LABELS = {
  surplusLinesTax: "Surplus lines tax",
  propertyTax: "Property tax",
  stampingFee: "Stamping fee",
  carrierFee: "Carrier fee",
  brokerFee: "Broker fee",
  surchargeFee: "Surcharge fee",
  techFee: "Tech fee",
  embeddedSecurityFee: "Embedded security fee",
};

function feeLinesFromTaxesFees(taxesFees) {
  if (!taxesFees || typeof taxesFees !== "object" || Array.isArray(taxesFees)) return [];
  const items = taxesFees.items;
  const lines = [];
  if (items && typeof items === "object" && !Array.isArray(items)) {
    Object.keys(items).forEach((key) => {
      if (!hasPricingAmount(items[key])) return;
      lines.push({ label: UWB_FEE_LABELS[key] || key, amount: items[key] });
    });
  }
  if (lines.length || !hasPricingAmount(taxesFees.total)) return lines;
  return [{ label: "Taxes and fees", amount: taxesFees.total }];
}

function pricingHasBreakdown(pricing) {
  if (hasPricingAmount(pricing?.basePremiumAmount)) return true;
  const lines = Array.isArray(pricing?.feeLines) ? pricing.feeLines : [];
  return lines.some((line) => line?.removed !== true && hasPricingAmount(line?.amount));
}

function keyFiguresFromDetail(detail) {
  const base = detail?.basePremium;
  const feeLines = feeLinesFromTaxesFees(detail?.taxesFees);
  if (!hasPricingAmount(base) && !feeLines.length) return null;
  return {
    basePremiumAmount: hasPricingAmount(base) ? base : null,
    feeLines,
    totalCost: hasPricingAmount(detail?.totalPremium) ? detail.totalPremium : null,
  };
}

/** Key Figures: quote.pricing when present, else pre-273 UWB detail breakdown. */
export function keyFiguresSourceFromQuote(quote) {
  const pricing = quote?.pricing || {};
  if (pricingHasBreakdown(pricing)) return pricing;
  return keyFiguresFromDetail(quote?.detail) || pricing;
}

/** An entered amount in whole cents; null when blank or not a valid amount. */
export function toCents(value) {
  const parsed = parseCurrencyAmount(value);
  if (parsed === null || parsed === "") return null;
  const negative = parsed.startsWith("-");
  const [whole, fraction = ""] = parsed.replace(/^[+-]/, "").split(".");
  const cents = Number(whole) * 100 + Number(`${fraction}00`.slice(0, 2));
  return negative ? -cents : cents;
}

/** Cents as the plain decimal string the API takes: "1250.00". */
export function centsToAmount(cents) {
  const negative = cents < 0;
  const abs = Math.abs(cents);
  const whole = Math.floor(abs / 100);
  const rest = String(abs % 100).padStart(2, "0");
  return `${negative ? "-" : ""}${whole}.${rest}`;
}

/** "$1,250.00", or "—" when there is no amount. Zero shows as "$0.00". */
export function formatMoney(value) {
  const formatted = formatCurrencyAmount(value);
  if (!formatted) return "—";
  return formatted.startsWith("-") ? `-$${formatted.slice(1)}` : `$${formatted}`;
}

/** "15.0000" as "15", "12.5000" as "12.5". */
export function plainRate(value) {
  const raw = text(value).trim();
  if (!raw) return "";
  return raw.includes(".") ? raw.replace(/\.?0+$/, "") : raw;
}

/** "YYYY-MM-DD" from a date or datetime string, else "". */
export const dateOnly = (value) => {
  const raw = text(value).trim();
  return /^\d{4}-\d{2}-\d{2}/.test(raw) ? raw.slice(0, 10) : "";
};

let lineSeq = 0;
const nextLineId = () => {
  lineSeq += 1;
  return `pricing-line-${lineSeq}`;
};

function lineFromRow(line, currencyCode) {
  const source = line.source || "broker";
  const original =
    line.sourceValue ||
    (isSourceValue(source) ? { label: line.label, amount: line.amount, feeType: line.feeType } : null);
  const label = text(line.label);
  const amount = formatCurrencyAmount(line.amount);
  const feeType = line.feeType || "other";
  const removed = line.removed === true;
  return {
    id: nextLineId(),
    key: line.key || "",
    source,
    currencyCode: line.currencyCode || currencyCode,
    label,
    amount,
    feeType,
    removed,
    changedAt: line.changedAt || null,
    original: original
      ? { label: text(original.label), amount: formatCurrencyAmount(original.amount), feeType: original.feeType }
      : null,
    // What the dialog opened with, so only what the broker touches is sent.
    opened: { label, amount, feeType, removed },
    restored: false,
  };
}

/** A new line the broker adds, in the quote's currency. */
export function newBrokerLine(currencyCode = "USD") {
  return {
    id: nextLineId(),
    key: "",
    source: "broker",
    currencyCode,
    label: "",
    amount: "",
    feeType: "other",
    removed: false,
    changedAt: null,
    original: null,
    opened: null,
    restored: false,
  };
}

/** The dialog's editable form for one quote's pricing. */
export function buildPricingForm(pricing) {
  const p = pricing || pricingFromApiQuote(null);
  const currencyCode = p.currencyCode || "USD";
  const premiumFromSource = isSourceValue(p.basePremiumSource) && p.basePremiumAmount !== null;
  const periodFromSource =
    isSourceValue(p.quotedPeriodSource) && Boolean(p.quotedEffectiveDate) && Boolean(p.quotedExpirationDate);
  const premium = formatCurrencyAmount(p.basePremiumAmount);
  const periodStart = dateOnly(p.quotedEffectiveDate);
  const periodEnd = dateOnly(p.quotedExpirationDate);
  return {
    currencyCode,
    premium,
    premiumOpened: premium,
    premiumRestored: false,
    premiumSource: p.basePremiumSource,
    // The source's own premium, while it is the source's (edited or not).
    premiumOriginal: premiumFromSource
      ? formatCurrencyAmount(p.basePremiumEdited ? p.basePremiumSourceAmount : p.basePremiumAmount)
      : null,
    premiumChangedAt: p.basePremiumChangedAt,
    periodStart,
    periodEnd,
    periodOpened: { start: periodStart, end: periodEnd },
    periodRestored: false,
    periodSource: p.quotedPeriodSource,
    periodOriginal: periodFromSource
      ? {
          start: dateOnly(p.quotedPeriodEdited ? p.quotedPeriodSourceEffectiveDate : p.quotedEffectiveDate),
          end: dateOnly(p.quotedPeriodEdited ? p.quotedPeriodSourceExpirationDate : p.quotedExpirationDate),
        }
      : null,
    periodChangedAt: p.quotedPeriodChangedAt,
    lines: (p.feeLines || []).map((line) => lineFromRow(line, currencyCode)),
    commissionRate: plainRate(p.commissionRate),
    commissionAmount: formatCurrencyAmount(p.commissionAmount),
    expiresOn: dateOnly(p.expiresOn),
    specialConditions: text(p.specialConditions),
    items: [...(p.itemsNeededToBind || [])],
  };
}

const sameAmount = (a, b) => toCents(a) === toCents(b);

/** A source line whose label, amount or type differs from what the source sent. */
export function lineEdited(line) {
  if (!line || !line.original || line.removed) return false;
  return (
    line.label.trim() !== line.original.label ||
    !sameAmount(line.amount, line.original.amount) ||
    line.feeType !== line.original.feeType
  );
}

export function premiumEdited(form) {
  return form.premiumOriginal !== null && !sameAmount(form.premium, form.premiumOriginal);
}

export function periodEdited(form) {
  return (
    form.periodOriginal !== null &&
    (form.periodStart !== form.periodOriginal.start || form.periodEnd !== form.periodOriginal.end)
  );
}

/** Restore a source line to what the source sent (an explicit restore on save). */
export function restoreLine(line) {
  line.label = line.original.label;
  line.amount = line.original.amount;
  line.feeType = line.original.feeType;
  line.removed = false;
  line.restored = true;
}

export function restorePremium(form) {
  form.premium = form.premiumOriginal;
  form.premiumRestored = true;
}

export function restorePeriod(form) {
  form.periodStart = form.periodOriginal.start;
  form.periodEnd = form.periodOriginal.end;
  form.periodRestored = true;
}

/**
 * Whether a line is in the totals: not removed, and from the premium's own source or
 * the broker when the premium came from a source (every line otherwise).
 */
export function lineCounted(form, line) {
  if (line.removed) return false;
  if (!isSourceValue(form.premiumSource)) return true;
  return line.source === form.premiumSource || line.source === "broker";
}

/**
 * Total Taxes & Fees and Total Cost for the form, in cents. `fees` is null when no
 * line counts, `total` when there is no premium; both are null while a counted amount
 * is not valid or the counted lines are in different currencies (as the API).
 */
export function pricingTotals(form) {
  const counted = form.lines.filter((line) => lineCounted(form, line));
  const currencies = new Set(counted.map((line) => line.currencyCode || form.currencyCode));
  if (form.currencyCode) currencies.add(form.currencyCode);
  if (currencies.size > 1) return { fees: null, total: null };
  const cents = counted.map((line) => toCents(line.amount));
  if (cents.some((value) => value === null)) return { fees: null, total: null };
  const fees = counted.length ? cents.reduce((sum, value) => sum + value, 0) : null;
  const base = toCents(form.premium);
  return { fees, total: base === null ? null : base + (fees || 0) };
}

const fromSourceLine = (line) => isSourceValue(line.source) && Boolean(line.key);

const amountOrNull = (value) => {
  const cents = toCents(value);
  return cents === null ? null : centsToAmount(cents);
};

/**
 * A source line's request entry, or null when the broker did not touch it (it then
 * keeps its stored state, so a callback that changed it meanwhile is not pinned).
 */
function sourceLineEntry(line) {
  const opened = line.opened || {};
  if (line.removed) return opened.removed ? null : { key: line.key, removed: true };
  if (line.restored && !lineEdited(line)) return { key: line.key, restore: true };
  const unchanged =
    !opened.removed &&
    line.label.trim() === opened.label &&
    sameAmount(line.amount, opened.amount) &&
    line.feeType === opened.feeType;
  if (unchanged) return null;
  return { key: line.key, label: line.label.trim(), amount: amountOrNull(line.amount), feeType: line.feeType };
}

/**
 * The lines the request lists, in order, each with its entry: the broker's own lines
 * (a removed one is left out) and the source lines the broker touched. The API names
 * a refused line by its index in this list.
 */
export function requestLines(form) {
  const out = [];
  form.lines.forEach((line) => {
    if (fromSourceLine(line)) {
      const entry = sourceLineEntry(line);
      if (entry) out.push({ line, entry });
    } else if (!line.removed) {
      out.push({
        line,
        entry: { label: line.label.trim(), amount: amountOrNull(line.amount), feeType: line.feeType },
      });
    }
  });
  return out;
}

/** The lines in use after a save: every line but a removed one. */
const activeLines = (form) => form.lines.filter((line) => !line.removed);

function amountError(value, label, { required = false } = {}) {
  const raw = text(value).trim();
  if (!raw) return required ? `${label} is required.` : "";
  if (/\.\d{3,}\s*$/.test(raw)) return `${label} can have at most 2 decimal places.`;
  const cents = toCents(raw);
  if (cents === null) return `${label} must be an amount such as 1,250.00.`;
  if (cents < 0) return `${label} cannot be negative.`;
  if (cents >= AMOUNT_LIMIT_CENTS) return `${label} must be less than 10,000,000,000.`;
  return "";
}

function dateError(value, label) {
  const raw = text(value).trim();
  if (!raw) return "";
  return moment(raw, "YYYY-MM-DD", true).isValid() ? "" : `${label} must be a date (MM/DD/YYYY).`;
}

/**
 * The form's problems as `{ field: message }`, empty when it can be saved. The same
 * limits the API enforces, so the broker sees them before saving. Field names:
 * `premium`, `periodStart`, `periodEnd`, `lines`, `<line id>.label`, `<line id>.amount`, `<line id>.feeType`,
 * `commissionRate`, `commissionAmount`, `expiresOn`, `specialConditions`, `items`, `item.<n>`.
 */
export function validatePricingForm(form) {
  const errors = {};
  const set = (field, message) => {
    if (message && !errors[field]) errors[field] = message;
  };

  if (form.premiumOriginal !== null && !text(form.premium).trim()) {
    set("premium", "Premium is required. Use Restore to bring back the carrier's premium.");
  }
  set("premium", amountError(form.premium, "Premium"));

  set("periodStart", dateError(form.periodStart, "Policy period start"));
  set("periodEnd", dateError(form.periodEnd, "Policy period end"));
  if (form.periodOriginal !== null && !form.periodStart && !form.periodEnd) {
    set("periodStart", "The policy period is required. Use Restore to bring back the carrier's period.");
  }
  if (!errors.periodStart && !errors.periodEnd) {
    if (form.periodStart && !form.periodEnd) set("periodEnd", "Enter the policy period end, or clear both dates.");
    if (form.periodEnd && !form.periodStart) set("periodStart", "Enter the policy period start, or clear both dates.");
    if (form.periodStart && form.periodEnd && form.periodStart > form.periodEnd) {
      set("periodEnd", "The policy period cannot end before it starts.");
    }
  }

  if (activeLines(form).length > PRICING_LIMITS.feeLines) {
    set("lines", `A quote can have at most ${PRICING_LIMITS.feeLines} fee lines.`);
  }
  form.lines.forEach((line) => {
    if (line.removed) return;
    const label = line.label.trim();
    if (!label) set(`${line.id}.label`, "Each fee needs a name.");
    else if (label.length > PRICING_LIMITS.label) {
      set(`${line.id}.label`, `A fee name can be at most ${PRICING_LIMITS.label} characters.`);
    }
    set(`${line.id}.amount`, amountError(line.amount, "Fee amount", { required: true }));
  });

  const rate = text(form.commissionRate).trim().replace(/%$/, "").trim();
  if (rate) {
    if (!RATE_RE.test(rate)) set("commissionRate", "Commission rate must be a percentage such as 15.");
    else if (Number(rate) > 100) set("commissionRate", "Commission rate must be between 0 and 100.");
    else if ((rate.split(".")[1] || "").length > 4) {
      set("commissionRate", "Commission rate can have at most 4 decimal places.");
    }
  }
  set("commissionAmount", amountError(form.commissionAmount, "Commission amount"));
  set("expiresOn", dateError(form.expiresOn, "Quote valid until"));

  if (text(form.specialConditions).trim().length > PRICING_LIMITS.specialConditions) {
    set(
      "specialConditions",
      `Special conditions can be at most ${PRICING_LIMITS.specialConditions.toLocaleString("en-US")} characters.`
    );
  }
  const items = form.items.filter((item) => text(item).trim());
  if (items.length > PRICING_LIMITS.items) {
    set("items", `List at most ${PRICING_LIMITS.items} items needed to bind coverage.`);
  }
  form.items.forEach((item, index) => {
    if (text(item).trim().length > PRICING_LIMITS.item) {
      set(`item.${index}`, `An item can be at most ${PRICING_LIMITS.item} characters.`);
    }
  });
  return errors;
}

/**
 * The PUT body for the form (see the API's `apply_broker_pricing`). A value a source
 * set (the premium, the period, each source line) is sent only when the broker
 * changed or restored it; the broker's own values, commission and terms always.
 */
export function buildPricingRequest(form) {
  const rate = text(form.commissionRate).trim().replace(/%$/, "").trim();
  const body = {};
  if (form.premiumOriginal === null) body.basePremiumAmount = amountOrNull(form.premium);
  else if (form.premiumRestored && !premiumEdited(form)) body.basePremiumAmount = null;
  else if (!sameAmount(form.premium, form.premiumOpened)) body.basePremiumAmount = amountOrNull(form.premium);

  const periodChanged = form.periodStart !== form.periodOpened.start || form.periodEnd !== form.periodOpened.end;
  if (form.periodOriginal === null || (periodChanged && !(form.periodRestored && !periodEdited(form)))) {
    body.quotedEffectiveDate = form.periodStart || null;
    body.quotedExpirationDate = form.periodEnd || null;
  } else if (form.periodRestored && !periodEdited(form)) {
    body.quotedEffectiveDate = null;
    body.quotedExpirationDate = null;
  }

  return {
    ...body,
    feeLines: requestLines(form).map(({ entry }) => entry),
    commissionRate: rate || null,
    commissionAmount: amountOrNull(form.commissionAmount),
    expiresOn: form.expiresOn || null,
    specialConditions: text(form.specialConditions).trim() || null,
    itemsNeededToBind: form.items.map((item) => text(item).trim()).filter(Boolean),
  };
}
